import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Send, RefreshCw, CheckCircle2, XCircle, Clock, Ban, Users, Loader2, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const statusMeta = {
  delivered:   { icon: CheckCircle2, cls: "text-emerald-600", bg: "bg-emerald-50", label: "Delivered" },
  sent:        { icon: Send, cls: "text-slate-500", bg: "bg-slate-50", label: "Sent to carrier" },
  queued:      { icon: Clock, cls: "text-amber-500", bg: "bg-amber-50", label: "Queued" },
  sending:     { icon: Clock, cls: "text-amber-500", bg: "bg-amber-50", label: "Sending" },
  accepted:    { icon: Clock, cls: "text-amber-500", bg: "bg-amber-50", label: "Accepted" },
  failed:      { icon: XCircle, cls: "text-red-600", bg: "bg-red-50", label: "Failed" },
  undelivered: { icon: AlertTriangle, cls: "text-red-500", bg: "bg-red-50", label: "Undelivered" },
  skipped:     { icon: Ban, cls: "text-amber-600", bg: "bg-amber-50", label: "Skipped (opted-out)" },
};

function StatusPill({ status }) {
  const m = statusMeta[status] || { icon: Clock, cls: "text-slate-400", bg: "bg-slate-50", label: status || "unknown" };
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold ${m.bg} ${m.cls}`}>
      <Icon className="w-3 h-3" /> {m.label}
    </span>
  );
}

export default function Broadcasts() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [refreshing, setRefreshing] = useState(false);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get("/broadcasts");
      setRows(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const refreshStatuses = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/twilio/refresh-status?limit=2000");
      toast.success(`Scanned ${data.scanned} messages · updated ${data.updated}`);
      await load(true);
    } catch (e) { toast.error(formatApiError(e, "Refresh failed")); }
    finally { setRefreshing(false); }
  };

  useEffect(() => {
    load();
    const iv = setInterval(() => load(true), 15000);
    return () => clearInterval(iv);
  }, []);

  const openDetail = async (id) => {
    setDetailLoading(true);
    setDetail({ id, loading: true });
    try {
      const { data } = await api.get(`/broadcasts/${id}`);
      setDetail(data);
    } catch (e) { toast.error(formatApiError(e)); setDetail(null); }
    finally { setDetailLoading(false); }
  };

  const removeBroadcast = async (id) => {
    if (!window.confirm("Remove this broadcast from history? (The individual texts stay in each conversation.)")) return;
    try {
      await api.delete(`/broadcasts/${id}`);
      toast.success("Removed");
      setRows((r) => r.filter((x) => x.id !== id));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="h-full overflow-y-auto thin-scroll" data-testid="broadcasts-page">
    <div className="max-w-6xl mx-auto p-4 sm:p-6">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Send className="w-6 h-6 text-[#D9232E]" />
            Broadcasts
          </h1>
          <p className="text-slate-500 text-sm mt-1">Every group text you've sent, with live delivery stats. Auto-refreshes every 15s.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={refreshStatuses} disabled={refreshing} data-testid="broadcasts-refresh-statuses" className="border-[#D9232E]/40 text-[#D9232E] hover:bg-red-50">
            {refreshing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1.5" />}
            Refresh delivery status
          </Button>
          <Button variant="outline" onClick={() => load()} data-testid="broadcasts-refresh">
            <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-16 text-slate-400"><Loader2 className="w-6 h-6 animate-spin inline mr-2" /> Loading...</div>
      ) : rows.length === 0 ? (
        <div className="text-center py-16 text-slate-400 text-sm border border-dashed border-slate-200 rounded-lg">
          No broadcasts yet. Send a group text and it'll show up here.
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((b) => {
            const total = b.total_recipients || 0;
            const delivered = b.delivered_count || 0;
            const failed = (b.failed_count || 0) + (b.failed_delivery || 0);
            const skipped = b.skipped_count || 0;
            const pending = b.pending_count || 0;
            const sentCarrier = b.sent_status_count || 0;
            const inflight = b.status === "queued" || pending > 0;
            const progressPct = total === 0 ? 0 : Math.round(((delivered + sentCarrier + failed + skipped) / total) * 100);
            return (
              <button
                key={b.id}
                type="button"
                onClick={() => openDetail(b.id)}
                data-testid={`broadcast-row-${b.id}`}
                className="w-full text-left bg-white border border-slate-200 rounded-lg p-4 hover:border-[#D9232E]/40 hover:shadow-sm transition-all"
              >
                <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-slate-900 truncate">{b.label}</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {new Date(b.created_at).toLocaleString()} · by {b.sender_name || "?"} · <Users className="w-3 h-3 inline" /> {total} recipient{total === 1 ? "" : "s"}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {inflight && <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 border-0"><Clock className="w-3 h-3 mr-1 animate-pulse" /> {pending} sending</Badge>}
                    <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-0"><CheckCircle2 className="w-3 h-3 mr-1" /> {delivered} delivered</Badge>
                    {failed > 0 && <Badge className="bg-red-100 text-red-700 hover:bg-red-100 border-0"><XCircle className="w-3 h-3 mr-1" /> {failed} failed</Badge>}
                    {skipped > 0 && <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100 border-0"><Ban className="w-3 h-3 mr-1" /> {skipped} skipped</Badge>}
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => { e.stopPropagation(); removeBroadcast(b.id); }}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); removeBroadcast(b.id); } }}
                      className="text-slate-300 hover:text-red-500 cursor-pointer p-1"
                      data-testid={`broadcast-delete-${b.id}`}
                      title="Remove from history"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </div>
                <div className="text-xs text-slate-600 line-clamp-2 mb-2">{b.body || "(attachment only)"}</div>
                <div className="w-full h-1.5 bg-slate-100 rounded overflow-hidden">
                  <div className="h-full bg-[#D9232E] transition-all duration-500" style={{ width: `${progressPct}%` }} />
                </div>
                <div className="text-[10px] text-slate-400 mt-1">{progressPct}% processed</div>
              </button>
            );
          })}
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-6 pt-5 pb-4 border-b border-slate-100">
            <DialogTitle className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <Send className="w-5 h-5 text-[#D9232E]" /> Broadcast detail
            </DialogTitle>
          </DialogHeader>
          {detailLoading || detail?.loading ? (
            <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : detail ? (
            <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
              <div className="px-6 py-4 bg-slate-50 border-b border-slate-100 shrink-0">
                <div className="text-xs text-slate-500 mb-1">Message</div>
                <div className="text-sm text-slate-900 whitespace-pre-wrap max-h-20 overflow-y-auto">{detail.body || "(attachment only)"}</div>
                <div className="mt-3 flex gap-2 flex-wrap text-[11px]">
                  <Badge variant="secondary"><Users className="w-3 h-3 mr-1" /> {detail.total_recipients} total</Badge>
                  <Badge className="bg-emerald-100 text-emerald-700 border-0"><CheckCircle2 className="w-3 h-3 mr-1" /> {detail.delivered_count || 0} delivered</Badge>
                  <Badge className="bg-slate-100 text-slate-600 border-0"><Send className="w-3 h-3 mr-1" /> {detail.sent_status_count || 0} sent to carrier</Badge>
                  <Badge className="bg-amber-100 text-amber-700 border-0"><Clock className="w-3 h-3 mr-1" /> {detail.pending_count || 0} pending</Badge>
                  <Badge className="bg-red-100 text-red-700 border-0"><XCircle className="w-3 h-3 mr-1" /> {(detail.failed_count || 0) + (detail.failed_delivery || 0)} failed</Badge>
                  <Badge className="bg-slate-100 text-slate-600 border-0"><Ban className="w-3 h-3 mr-1" /> {detail.skipped_count || 0} skipped</Badge>
                </div>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto thin-scroll">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-white border-b border-slate-200 text-[11px] uppercase tracking-wide text-slate-500 z-10">
                    <tr>
                      <th className="text-left px-4 py-2">Recipient</th>
                      <th className="text-left px-4 py-2">Phone</th>
                      <th className="text-left px-4 py-2">Status</th>
                      <th className="text-left px-4 py-2">Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(detail.recipients || []).map((r) => (
                      <tr key={r.contact_id + (r.message_id || "")} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`broadcast-recipient-${r.contact_id}`}>
                        <td className="px-4 py-2 font-medium text-slate-900">{r.name}</td>
                        <td className="px-4 py-2 text-slate-600">{r.phone}</td>
                        <td className="px-4 py-2"><StatusPill status={r.status} /></td>
                        <td className="px-4 py-2 text-xs text-slate-500">{r.error_code || ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(!detail.recipients || detail.recipients.length === 0) && (
                  <div className="p-8 text-center text-sm text-slate-400">Building recipient list...</div>
                )}
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
    </div>
  );
}
